import { useEffect, useState, useRef } from 'react';
import { toPng } from 'html-to-image';
import { Share2, Download, EyeOff, Eye, Loader2, TrendingUp, TrendingDown, Minus, Clock } from 'lucide-react';
import { toast } from 'sonner';
import ModalCloseButton from './ModalCloseButton';

// Share-card renderer for the Watchlist Time Machine bar. Renders a
// branded, fixed-size 540×N PNG card for posting to Reddit / X.
//
// Pattern mirrors DripShareModal — same html-to-image capture, same
// hide-dollars toggle, same Web Share API + download fallback.
//
// `payload` must include the same fields the bar gets from
// /api/watchlist/time-machine/{ticker}: ticker, current_value,
// total_return_pct, verdict, current_shares, initial_shares,
// entry_price, entry_date, current_date, drip_buys,
// total_dividends_received, annual_dividend_income, yoc, benchmarks.

const VERDICT_DISPLAY = {
  great_buy:      { label: 'Great Buy',      color: '#34d399', icon: TrendingUp,   bg: 'rgba(52, 211, 153, 0.12)' },
  good_buy:       { label: 'Good Buy',       color: '#22c55e', icon: TrendingUp,   bg: 'rgba(34, 197, 94, 0.12)' },
  lukewarm:       { label: 'Lukewarm',       color: '#f59e0b', icon: Minus,        bg: 'rgba(245, 158, 11, 0.14)' },
  underperformed: { label: 'Underperformed', color: '#fb923c', icon: TrendingDown, bg: 'rgba(251, 146, 60, 0.14)' },
  bad_buy:        { label: 'Bad Buy',        color: '#f43f5e', icon: TrendingDown, bg: 'rgba(244, 63, 94, 0.14)' },
  unknown:        { label: 'Unknown',        color: '#94a3b8', icon: Minus,        bg: 'rgba(148, 163, 184, 0.14)' },
};

function maskDollar() { return '$●●●'; }

function fmtDollar(v, hide, decimals = 0) {
  if (hide) return maskDollar();
  const n = Number(v) || 0;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
}

function fmtPct(v, decimals = 1) {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  return `${n >= 0 ? '+' : ''}${n.toFixed(decimals)}%`;
}

function BenchmarkPill({ name, stockReturn, benchmarkReturn }) {
  if (benchmarkReturn == null || stockReturn == null) {
    return (
      <div style={{ background: 'rgba(30, 41, 59, 0.55)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: 10, padding: '8px 12px' }}>
        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', fontWeight: 700 }}>vs {name}</div>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#94a3b8', marginTop: 2, fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace' }}>—</div>
      </div>
    );
  }
  const delta = stockReturn - benchmarkReturn;
  const pos = delta >= 0;
  return (
    <div style={{ background: 'rgba(30, 41, 59, 0.55)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: 10, padding: '8px 12px' }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', fontWeight: 700 }}>vs {name}</div>
      <div style={{ fontSize: 16, fontWeight: 800, color: pos ? '#4ade80' : '#fb7185', marginTop: 2, fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace' }}>
        {pos ? '+' : ''}{delta.toFixed(1)}%
      </div>
    </div>
  );
}

function ShareCard({ payload, hideDollars, cardRef }) {
  const verdict = VERDICT_DISPLAY[payload.verdict] || VERDICT_DISPLAY.unknown;
  const VIcon = verdict.icon;
  const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const isProfit = (payload.total_return_pct ?? 0) >= 0;
  const periodMonths = payload.fallback_period_months || 12;
  const periodLabel = periodMonths === 12 ? '1 year ago' : `${periodMonths} months ago`;

  return (
    <div
      ref={cardRef}
      data-testid="time-machine-share-card"
      style={{
        width: '540px',
        background: 'linear-gradient(145deg, #0f172a 0%, #0b1220 60%, #020617 100%)',
        color: '#f8fafc',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      }}
      className="rounded-xl p-6 shadow-2xl"
    >
      {/* Brand header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/dividrip-icon.png" alt="" width="36" height="36" style={{ width: 36, height: 36 }} />
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em' }}>DiviDrip</div>
            <div style={{ fontSize: 10, color: '#fb923c', letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Clock style={{ width: 11, height: 11 }} />
              Time Machine · {periodLabel}
            </div>
          </div>
        </div>
        <div style={{ fontSize: 10, color: '#64748b' }}>{today}</div>
      </div>

      {/* Title — ticker + headline result */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 36, fontWeight: 900, letterSpacing: '-0.02em', color: '#22d3ee' }}>
          {payload.ticker}
        </div>
        <div style={{ fontSize: 13, color: '#cbd5e1', marginTop: 4 }}>
          $1,000 invested {periodLabel} (DRIP on) →{' '}
          <span style={{ color: isProfit ? '#4ade80' : '#fb7185', fontWeight: 800, fontFamily: 'ui-monospace, monospace' }}>
            {fmtDollar(payload.current_value, hideDollars, 0)}
          </span>{' '}
          <span style={{ color: isProfit ? '#4ade80' : '#fb7185', fontFamily: 'ui-monospace, monospace', fontWeight: 700 }}>
            ({fmtPct(payload.total_return_pct, 2)})
          </span>
        </div>
      </div>

      {/* Verdict pill */}
      <div style={{ marginBottom: 18 }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 14px',
            borderRadius: 999,
            background: verdict.bg,
            border: `1px solid ${verdict.color}66`,
            color: verdict.color,
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          <VIcon style={{ width: 14, height: 14 }} />
          {verdict.label}
        </div>
      </div>

      {/* Key metrics — 2 columns × 2 rows */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
        <MetricCell label="Shares Bought" value={(Number(payload.initial_shares) || 0).toFixed(4)} sub={`@ ${fmtDollar(payload.entry_price, false, 2)}`} />
        <MetricCell label="Shares Now" value={(Number(payload.current_shares) || 0).toFixed(4)} sub={`+${((payload.current_shares ?? 0) - (payload.initial_shares ?? 0)).toFixed(4)} from DRIP`} accent />
        <MetricCell label="DRIP Buys" value={`${payload.drip_buys || 0} · ${fmtDollar(payload.total_dividends_received, hideDollars, 2)}`} accent />
        <MetricCell label="Forward Income" value={payload.annual_dividend_income != null ? `${fmtDollar(payload.annual_dividend_income, hideDollars, 0)}/yr` : '—'} />
      </div>

      {/* Benchmarks — 3 across, the headline social-share callout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 14 }}>
        <BenchmarkPill name="SPY"  stockReturn={payload.total_return_pct} benchmarkReturn={payload.benchmarks?.SPY} />
        <BenchmarkPill name="SCHD" stockReturn={payload.total_return_pct} benchmarkReturn={payload.benchmarks?.SCHD} />
        <BenchmarkPill name="VYM"  stockReturn={payload.total_return_pct} benchmarkReturn={payload.benchmarks?.VYM} />
      </div>

      {/* Assumptions footer */}
      <div style={{ borderTop: '1px solid rgba(148, 163, 184, 0.18)', paddingTop: 12, fontSize: 11, color: '#94a3b8', lineHeight: 1.5 }}>
        Entry: {(Number(payload.initial_shares) || 0).toFixed(4)} sh @ {fmtDollar(payload.entry_price, false, 2)} on {payload.entry_date} ·
        Latest data: {payload.current_date} · YoC {payload.yoc != null ? `${Number(payload.yoc).toFixed(2)}%` : '—'}
      </div>

      {/* Brand footer */}
      <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 10 }}>
        <span style={{ color: '#64748b' }}>Past performance, not financial advice.</span>
        <span style={{ color: '#22d3ee', fontWeight: 700, letterSpacing: '0.02em' }}>twylightcrow.com</span>
      </div>
    </div>
  );
}

function MetricCell({ label, value, sub, accent }) {
  return (
    <div style={{ background: 'rgba(30, 41, 59, 0.55)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: 10, padding: '10px 12px' }}>
      <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', fontWeight: 700 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 800,
          marginTop: 4,
          fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
          color: accent ? '#4ade80' : '#f8fafc',
          letterSpacing: '-0.01em',
        }}
      >
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2, fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace' }}>
          {sub}
        </div>
      )}
    </div>
  );
}

export default function TimeMachineShareModal({ open, onClose, payload }) {
  const [hideDollars, setHideDollars] = useState(false);
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);
  // Dynamic preview scaling — see TriangleShareModal for the full
  // rationale. The 540px capture target stays untouched (so the PNG
  // is pixel-perfect) but the on-screen preview is scaled to fit
  // whatever width the modal currently has so the card never gets
  // cropped on phone viewports.
  const previewBoxRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(1);
  const [cardNaturalHeight, setCardNaturalHeight] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    const recompute = () => {
      const box = previewBoxRef.current;
      const card = cardRef.current;
      if (!box || !card) return;
      const available = box.clientWidth;
      const scale = Math.min(1, available / 540);
      setPreviewScale(scale > 0 ? scale : 1);
      setCardNaturalHeight(card.scrollHeight);
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    if (previewBoxRef.current) ro.observe(previewBoxRef.current);
    if (cardRef.current) ro.observe(cardRef.current);
    window.addEventListener('resize', recompute);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', recompute);
    };
  }, [open, payload, hideDollars]);

  if (!open) return null;

  const filenameBase = `dividrip-timemachine-${(payload?.ticker || 'stock').toLowerCase()}`;

  const capture = async () => {
    if (!cardRef.current) throw new Error('card not mounted');
    const dataUrl = await toPng(cardRef.current, {
      pixelRatio: 2,
      cacheBust: true,
      backgroundColor: '#0b1220',
    });
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return { dataUrl, blob };
  };

  const handleShare = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { blob, dataUrl } = await capture();
      const file = new File([blob], `${filenameBase}.png`, { type: 'image/png' });
      const canUseShare =
        typeof navigator !== 'undefined' &&
        typeof navigator.share === 'function' &&
        typeof navigator.canShare === 'function' &&
        navigator.canShare({ files: [file] });
      if (canUseShare) {
        await navigator.share({
          files: [file],
          title: 'DiviDrip Time Machine',
          text: `$1,000 in ${payload?.ticker} 1y ago → ${fmtDollar(payload?.current_value, false, 0)} (${fmtPct(payload?.total_return_pct, 1)})`,
        });
        toast.success('Shared!');
      } else {
        triggerDownload(dataUrl, `${filenameBase}.png`);
        toast.success('Downloaded — share manually', {
          description: 'Your browser doesn\'t support native share for images. The file is in your Downloads.',
        });
      }
    } catch (err) {
      if (err && err.name !== 'AbortError') {
        toast.error('Unable to generate share image', { description: String(err.message || err) });
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { dataUrl } = await capture();
      triggerDownload(dataUrl, `${filenameBase}.png`);
      toast.success('Saved to Downloads');
    } catch (err) {
      toast.error('Unable to generate PNG', { description: String(err.message || err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4"
      data-testid="tm-share-modal-overlay"
    >
      <div className="bg-card border rounded-xl shadow-2xl w-full max-w-[620px] max-h-[92vh] overflow-y-auto pb-24 sm:pb-4" data-testid="tm-share-modal">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-card z-10">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-500" />
            <h2 className="text-base font-bold">Share your Time Machine result</h2>
          </div>
          <ModalCloseButton onClick={onClose} testId="tm-share-modal-close" />
        </div>

        <div className="p-4 space-y-4">
          <p className="text-xs text-muted-foreground">
            Only the card shown below is captured — nothing else on your screen.
          </p>

          <button
            type="button"
            onClick={() => setHideDollars(v => !v)}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-semibold transition-colors ${
              hideDollars
                ? 'bg-primary/10 border-primary/40 text-primary'
                : 'bg-muted border-border text-foreground hover:bg-muted/70'
            }`}
            data-testid="tm-share-toggle-hide-dollars"
            aria-pressed={hideDollars}
          >
            {hideDollars ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {hideDollars ? 'Dollar amounts hidden' : 'Hide dollar amounts'}
          </button>
          {hideDollars && (
            <p className="text-[11px] text-muted-foreground -mt-2 pl-1">
              Dollar figures are masked on the shared card. Percentages, ticker, and verdict are still visible.
            </p>
          )}

          <div
            ref={previewBoxRef}
            className="w-full rounded-lg border bg-muted/30 p-3 overflow-hidden flex justify-center"
            style={{ height: cardNaturalHeight > 0 ? cardNaturalHeight * previewScale + 24 : undefined }}
          >
            <div
              style={{
                transformOrigin: 'top center',
                transform: `scale(${previewScale})`,
                width: 540,
                flex: '0 0 540px',
              }}
            >
              <ShareCard payload={payload} hideDollars={hideDollars} cardRef={cardRef} />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={handleShare}
              disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-lg bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-colors disabled:opacity-60"
              data-testid="tm-share-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
              Share
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-lg border bg-background font-semibold text-sm hover:bg-muted transition-colors disabled:opacity-60"
              data-testid="tm-share-download-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download PNG
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function triggerDownload(dataUrl, filename) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
